import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import api from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'accessToken';
const USER_KEY = 'user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          // Try to refresh token and get fresh user data before setting state
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const result = await authService.refresh(refreshToken);
              localStorage.setItem(TOKEN_KEY, result.accessToken);
              setAccessToken(result.accessToken);

              // Fetch fresh user profile to pick up DB changes (e.g. profile_completed)
              const profileRes = await api.get('/users/profile');
              const freshUser = profileRes.data.data.user;
              setUser(freshUser);
              localStorage.setItem(USER_KEY, JSON.stringify(freshUser));

              // Redirect to profile setup if incomplete
              if (freshUser.profile_completed === false && window.location.pathname !== '/settings') {
                window.location.href = '/settings?setup=1';
                return;
              }
            } catch (refreshError) {
              // Token refresh or profile fetch failed, clear auth state
              clearAuthState();
            }
          } else {
            // No refresh token, use stored data as-is
            setAccessToken(storedToken);
            setUser(JSON.parse(storedUser));
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('refreshToken');
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await authService.login(email, password);

      setUser(result.user);
      setAccessToken(result.accessToken);

      localStorage.setItem(TOKEN_KEY, result.accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(result.user));
      localStorage.setItem('refreshToken', result.refreshToken);

      // Force redirect if profile incomplete
      if (result.user.profile_completed === false) {
        window.location.href = '/settings?setup=1';
        return result;
      }

      return result;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  }, []);

  const register = useCallback(async (email, password, firstName, lastName) => {
    setError(null);
    try {
      const result = await authService.register(email, password, firstName, lastName);
      return result;
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const result = await authService.refresh(refreshToken);
      setAccessToken(result.accessToken);
      localStorage.setItem(TOKEN_KEY, result.accessToken);

      return result.accessToken;
    } catch (err) {
      clearAuthState();
      throw err;
    }
  }, [clearAuthState]);

  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    try {
      const result = await authService.requestPasswordReset(email);
      return result;
    } catch (err) {
      setError(err.message || 'Password reset request failed');
      throw err;
    }
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    setError(null);
    try {
      const result = await authService.resetPassword(token, password);
      return result;
    } catch (err) {
      setError(err.message || 'Password reset failed');
      throw err;
    }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    setError(null);
    try {
      const result = await authService.verifyEmail(token);
      return result;
    } catch (err) {
      setError(err.message || 'Email verification failed');
      throw err;
    }
  }, []);

  const loginWithGoogle = useCallback(async (returnPath = '/events') => {
    try {
      const url = await authService.getGoogleAuthUrl(returnPath);
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to start Google sign-in');
      throw err;
    }
  }, []);

  const handleGoogleCallback = useCallback((accessToken, refreshToken, user) => {
    setUser(user);
    setAccessToken(accessToken);

    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem('refreshToken', refreshToken);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  }, []);

  const value = {
    user,
    accessToken,
    loading,
    error,
    isAuthenticated: !!user && !!accessToken,
    login,
    register,
    logout,
    refreshAccessToken,
    requestPasswordReset,
    resetPassword,
    verifyEmail,
    loginWithGoogle,
    handleGoogleCallback,
    updateUser,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
